import { useEffect, useState } from "react";
import { Edit2, Plus, Save, X, Eye, EyeOff } from "lucide-react";
import { api, NodeTypeTemplate } from "../lib/api";
import { ShapePicker } from "./ShapePicker";
import { Spinner } from "./Spinner";
import { InlineAlert } from "./InlineAlert";

interface TemplateWithVisibility extends NodeTypeTemplate {
  is_visible: boolean;
}

interface UnifiedTemplateManagerProps {
  structureId: string;
  onVisibilityChanged?: () => void;
}

export function UnifiedTemplateManager({ structureId, onVisibilityChanged }: UnifiedTemplateManagerProps) {
  const [templates, setTemplates] = useState<TemplateWithVisibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<NodeTypeTemplate>>({});
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [visibilityChanges, setVisibilityChanges] = useState<Map<string, boolean>>(new Map());
  const [hasVisibilityChanges, setHasVisibilityChanges] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getStructureTemplateVisibility(structureId);
      // Filter out archived templates
      setTemplates(data.filter(t => !t.is_archived));
      setVisibilityChanges(new Map());
      setHasVisibilityChanges(false);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [structureId]);

  const toggleVisibility = (templateId: string, currentVisibility: boolean) => {
    const newVisibility = !currentVisibility;
    setVisibilityChanges((prev) => {
      const updated = new Map(prev);
      updated.set(templateId, newVisibility);
      return updated;
    });
    setHasVisibilityChanges(true);

    // Update local state for immediate UI feedback
    setTemplates((prev) =>
      prev.map((t) => (t.id === templateId ? { ...t, is_visible: newVisibility } : t))
    );
  };

  const saveVisibilityChanges = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates = Array.from(visibilityChanges.entries()).map(([template_id, is_visible]) => ({
        template_id,
        is_visible,
      }));

      await api.updateStructureTemplateVisibility(structureId, updates);
      setVisibilityChanges(new Map());
      setHasVisibilityChanges(false);
      if (onVisibilityChanged) {
        onVisibilityChanged();
      }
      await loadTemplates();
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (template: NodeTypeTemplate) => {
    setEditingId(template.id);
    setEditForm({
      label: template.label,
      default_color: template.default_color,
      default_text_color: template.default_text_color,
      shape: template.shape,
      sort_order: template.sort_order,
    });
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditForm({
      key: "",
      label: "",
      default_color: "#6366f1",
      default_text_color: "#ffffff",
      shape: "rectangle",
      sort_order: templates.length,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setEditForm({});
  };

  const saveTemplate = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        if (!editForm.key || !editForm.label) {
          setError("Key und Label sind Pflichtfelder");
          setSaving(false);
          return;
        }
        await api.createTemplate({
          key: editForm.key,
          label: editForm.label,
          default_color: editForm.default_color || "#6366f1",
          default_text_color: editForm.default_text_color || "#ffffff",
          shape: editForm.shape || "rectangle",
          sort_order: editForm.sort_order || 0,
        });
        setIsCreating(false);
      } else if (editingId) {
        await api.updateTemplate(editingId, {
          label: editForm.label,
          default_color: editForm.default_color,
          default_text_color: editForm.default_text_color,
          shape: editForm.shape,
          sort_order: editForm.sort_order,
        });
        setEditingId(null);
      }
      setEditForm({});
      await loadTemplates();
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // Group templates by structure_type_key
  const groupedTemplates = templates.reduce((acc, template) => {
    const group = template.structure_type_key || "universal";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(template);
    return acc;
  }, {} as Record<string, TemplateWithVisibility[]>);

  const groupLabels: Record<string, string> = {
    organigramm: "Organigramm",
    prozess: "Prozess",
    mindmap: "Mindmap",
    universal: "Universal",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Lade Templates..." size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <button className="btn btn-primary btn-sm" onClick={startCreate} disabled={isCreating}>
          <Plus className="h-4 w-4" /> Neuer Typ
        </button>
        {hasVisibilityChanges && (
          <div className="flex gap-2">
            <button
              onClick={() => loadTemplates()}
              disabled={saving}
              className="btn btn-secondary btn-sm"
            >
              Abbrechen
            </button>
            <button
              onClick={saveVisibilityChanges}
              disabled={saving}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
              Sichtbarkeit speichern
            </button>
          </div>
        )}
      </div>

      {error && (
        <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} />
      )}

      {/* Create Form */}
      {isCreating && (
        <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-panel-2)] space-y-3">
          <h5 className="text-sm font-semibold">Neuen Node-Typ erstellen</h5>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Key (einmalig) *</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.key || ""}
                onChange={(e) => setEditForm({ ...editForm, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                placeholder="z.B. arbeitsgruppe"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Label *</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.label || ""}
                onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                placeholder="z.B. Arbeitsgruppe"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Farbe</label>
              <input
                type="color"
                className="w-full h-10 mt-1 rounded border border-[var(--color-border)] cursor-pointer"
                value={editForm.default_color || "#6366f1"}
                onChange={(e) => setEditForm({ ...editForm, default_color: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Textfarbe</label>
              <input
                type="color"
                className="w-full h-10 mt-1 rounded border border-[var(--color-border)] cursor-pointer"
                value={editForm.default_text_color || "#ffffff"}
                onChange={(e) => setEditForm({ ...editForm, default_text_color: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-[var(--color-muted)]">Form</label>
            <div className="mt-1">
              <ShapePicker
                value={editForm.shape || "rectangle"}
                onChange={(shape) => setEditForm({ ...editForm, shape })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
              <X className="h-4 w-4" /> Abbrechen
            </button>
            <button className="btn btn-primary btn-sm" onClick={saveTemplate} disabled={saving}>
              {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />} Erstellen
            </button>
          </div>
        </div>
      )}

      {/* Grouped Templates List */}
      <div className="space-y-6">
        {Object.entries(groupedTemplates).map(([groupKey, groupTemplates]) => (
          <div key={groupKey} className="space-y-2">
            <h4 className="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">
              {groupLabels[groupKey] || groupKey}
            </h4>
            <div className="space-y-2">
              {groupTemplates.map((template) => {
                const isVisible = template.is_visible;

                return editingId === template.id ? (
                  // Edit Form - Inline
                  <div key={template.id} className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-panel-2)] space-y-3">
                    <div>
                      <label className="text-xs text-[var(--color-muted)]">Label *</label>
                      <input
                        type="text"
                        className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                        value={editForm.label || ""}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-[var(--color-muted)]">Farbe</label>
                        <input
                          type="color"
                          className="w-full h-10 mt-1 rounded border border-[var(--color-border)] cursor-pointer"
                          value={editForm.default_color || "#6366f1"}
                          onChange={(e) => setEditForm({ ...editForm, default_color: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[var(--color-muted)]">Textfarbe</label>
                        <input
                          type="color"
                          className="w-full h-10 mt-1 rounded border border-[var(--color-border)] cursor-pointer"
                          value={editForm.default_text_color || "#ffffff"}
                          onChange={(e) => setEditForm({ ...editForm, default_text_color: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-[var(--color-muted)]">Form</label>
                      <div className="mt-1">
                        <ShapePicker
                          value={editForm.shape || "rectangle"}
                          onChange={(shape) => setEditForm({ ...editForm, shape })}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
                        <X className="h-4 w-4" /> Abbrechen
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={saveTemplate} disabled={saving}>
                        {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />} Speichern
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display Mode - Combined
                  <div
                    key={template.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border transition-all
                      ${isVisible
                        ? "bg-white dark:bg-slate-800 border-[var(--color-border)]"
                        : "bg-[var(--color-panel-2)] border-[var(--color-border)] opacity-60"
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className="w-10 h-10 rounded flex items-center justify-center text-xs font-medium"
                        style={{
                          backgroundColor: template.default_color,
                          color: template.default_text_color,
                        }}
                      >
                        Ab
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{template.label}</div>
                        <div className="text-xs text-[var(--color-muted)]">
                          {template.shape || "rectangle"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className="p-2 hover:bg-[var(--color-panel-2)] rounded transition-colors"
                        onClick={() => startEdit(template)}
                        title="Bearbeiten"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleVisibility(template.id, isVisible)}
                        className={`
                          p-2 rounded transition-all
                          ${isVisible
                            ? "text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                            : "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          }
                        `}
                        title={isVisible ? "Ausblenden" : "Einblenden"}
                      >
                        {isVisible ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {hasVisibilityChanges && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Ungespeicherte Änderungen an der Sichtbarkeit. Klicke "Sichtbarkeit speichern", um zu übernehmen.
          </p>
        </div>
      )}

      {templates.length === 0 && (
        <div className="text-center py-8 text-[var(--color-muted)]">
          <p className="text-sm">Keine Templates vorhanden.</p>
          <button className="btn btn-primary btn-sm mt-4" onClick={startCreate}>
            <Plus className="h-4 w-4" /> Ersten Typ erstellen
          </button>
        </div>
      )}
    </div>
  );
}
